import { useState, useRef, useEffect } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import CompoundingYieldIcon from '~/@mid_ui/icons/CompoundingYieldIcon'
import type { ChartDataItem } from '../project-detail-chart/types'
import { CustomActiveDot } from './CustomActiveDot'
import { ChartTooltip } from './ChartTooltip'
import { ProjectPaymentTypeTooltip } from '~/@pages_investing/projects/_projects_ui/project-payment-type-label/ProjectPaymentTypeTooltip'
import { getProjectPaymentTypeLabel } from '~/@pages_investing/projects/_projects_ui/project-payment-type-label/getProjectPaymentTypeLabel'

type Props = {
  chartData: Array<ChartDataItem>
  variant: 'black' | 'white'
}

type TextAnchor = 'start' | 'middle' | 'end' | 'inherit'

export function ProjectCompoundedRevenueChart({
  chartData,
  variant = 'white',
}: Props) {
  const [hoverData, setHoverData] = useState<ChartDataItem | null>(null)

  const chartRef = useRef<HTMLDivElement>(null)
  const touchStartPos = useRef<{ x: number; y: number } | null>(null)
  const isScrollingRef = useRef<boolean | null>(null)

  const handleMouseMove = (state: any) => {
    if (state.activePayload && state.activePayload.length) {
      setHoverData(state.activePayload[0].payload)
    }
  }

  useEffect(() => {
    const element = chartRef.current
    if (!element) return

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0]
      touchStartPos.current = { x: touch.clientX, y: touch.clientY }
      isScrollingRef.current = null // Reset pri každom novom dotyku
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStartPos.current) return

      if (isScrollingRef.current === true) return

      const touch = e.touches[0]
      const deltaX = Math.abs(touch.clientX - touchStartPos.current.x)
      const deltaY = Math.abs(touch.clientY - touchStartPos.current.y)

      // Rozhodovacia logika (10px prah)
      if (isScrollingRef.current === null && (deltaX > 10 || deltaY > 10)) {
        if (deltaY > deltaX) {
          isScrollingRef.current = true
        } else {
          isScrollingRef.current = false
        }
      }

      // Ak sme vyhodnotili, že používateľ posúva graf (horizontálne), zrušíme natívny scroll
      if (isScrollingRef.current === false) {
        if (e.cancelable) {
          e.preventDefault()
        }
      }
    }

    const handleTouchEnd = () => {
      touchStartPos.current = null
      isScrollingRef.current = null
    }

    element.addEventListener('touchstart', handleTouchStart, { passive: true })
    element.addEventListener('touchmove', handleTouchMove, { passive: false })
    element.addEventListener('touchend', handleTouchEnd)

    return () => {
      element.removeEventListener('touchstart', handleTouchStart)
      element.removeEventListener('touchmove', handleTouchMove)
      element.removeEventListener('touchend', handleTouchEnd)
    }
  }, [])

  const allMonthTicks = new Array(chartData.length).fill(0).map((_, i) => i)
  const xTicks = allMonthTicks.filter((month, index) => {
    return month % 12 === 0 || index + 1 === allMonthTicks.length
  })

  function CustomTick(props: any) {
    const { x, y, payload } = props
    if (!xTicks.includes(payload.value)) return null

    const textAnchor: TextAnchor = (() => {
      if (payload.value === xTicks[0]) return 'start'
      if (payload.value === xTicks[xTicks.length - 1]) return 'end'
      return 'middle'
    })()

    const isMobile = window.innerWidth < 640
    const tickLabel =
      payload.value === 0
        ? isMobile
          ? '0 M'
          : 'Začiatok'
        : `${payload.value} ${isMobile ? 'M' : 'MESIACOV'}`

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={10}
          textAnchor={textAnchor}
          fill="var(--color-muted)"
          fontSize={12}
          fontWeight={600}>
          {tickLabel}
        </text>
      </g>
    )
  }

  return (
    <>
      <div className="flex flex-row items-center gap-4">
        <div className="flex flex-col items-center gap-1">
          <div className="flex flex-row items-center gap-1">
            <p className="text-sm font-semibold text-muted">
              Rastúce mesačné úročenie
            </p>
            <CompoundingYieldIcon arrowColor="#757575" percentColor="#757575" />
            <ProjectPaymentTypeTooltip
              paymentTypeLabel={getProjectPaymentTypeLabel('monthly', true)}
            />
          </div>
          <div className="w-full h-1 bg-green rounded-sm" />
        </div>
        <div className="flex flex-col items-center gap-1">
          <div className="flex flex-row items-center gap-1">
            <p className="text-sm font-semibold text-muted">
              Mesačne vyplácané úroky %
            </p>
          </div>
          <div className="w-full h-1 border-t-2 border-dashed border-border" />
        </div>
      </div>

      <div
        ref={chartRef}
        className="w-[calc(100%+16px)] h-[400px] relative -ml-2"
        style={{
          touchAction: 'pan-y',
        }}>
        <ResponsiveContainer width="100%">
          <AreaChart
            data={chartData}
            margin={{ right: 8, left: 8, bottom: 20, top: 20 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHoverData(null)}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={variant === 'black' ? '#201f1f' : '#e5e7eb'}
              horizontal={true}
              vertical={true}
            />
            <XAxis
              dataKey="months"
              ticks={allMonthTicks}
              axisLine={false}
              tickLine={false}
              tick={(props) => <CustomTick {...props} />}
              interval={0}
            />
            <YAxis
              domain={['dataMin', 'auto']}
              axisLine={false}
              tickLine={false}
              tick={false}
              width={0}
            />
            {chartData
              .filter((_, index) => index % 12 === 0)
              .map((item) => (
                <ReferenceLine
                  key={`year-line-${item.months}`}
                  stroke={variant === 'black' ? '#404040' : '#cccccc'}
                  strokeWidth={1}
                  segment={[
                    { x: item.months, y: 0 },
                    { x: item.months, y: item['Rastúce mesačné úročenie'] },
                  ]}
                />
              ))}
            <Tooltip
              content={
                <ChartTooltip
                  {...{
                    active: undefined,
                    payload: undefined,
                    label: undefined,
                  }}
                  variant={variant}
                />
              }
              cursor={false}
            />
            <defs>
              <linearGradient id="colorGreen" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#50F000" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#50F000" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="Rastúce mesačné úročenie"
              stroke="#50F000"
              fill="url(#colorGreen)"
              strokeWidth={3}
              activeDot={<CustomActiveDot />}
              dot={false}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="Mesačne vyplácané úroky %"
              stroke="#9ca3af"
              strokeWidth={2}
              strokeDasharray="5 5"
              fill="transparent"
              activeDot={<CustomActiveDot />}
              dot={false}
              isAnimationActive={false}
            />
            {hoverData && (
              <ReferenceLine
                x={hoverData.months}
                stroke="#50F000"
                strokeWidth={2}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-sm text-muted flex items-center">
          <sup className="inline-block w-3">1</sup>
          Výška rastúceho výnosu v čase
        </p>
        <p className="text-sm text-muted flex items-center">
          <sup className="inline-block w-3">2</sup>
          Rastúci výnos kumulatívne
        </p>
        <p className="text-sm text-muted flex items-center">
          <sup className="inline-block w-3">3</sup>
          Obyčajný výnos kumulatívne
        </p>
      </div>
    </>
  )
}
